import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import { BsInstagram, BsPlayFill } from "react-icons/bs";

// Components
import ButtonsPrevNext from "../../Buttons/ButtonsPrevNext";

// Constants
import {
    FrontPresenterCuration,
    frontPresenterCuration,
} from "../../../Constants/FrontPresenterCuration";

// Helpers
import {
    getInstagramReelEmbedUrl,
    getSafeInstagramUrl,
} from "../../../Helpers/Instagram";

// Hooks
import useFetchPublicAuthors from "../../../Hooks/Authors/useFetchPublicAuthors";

// Models
import PublicAuthorDTO from "../../../Models/DTOs/PublicAuthorDTO";

interface Presenter {
    author: PublicAuthorDTO;
    curation?: FrontPresenterCuration;
}

interface InstagramProfile {
    handle: string;
    profileUrl: string;
}

export interface FrontPresentersSectionProps {
    onOpenReel?: (author: PublicAuthorDTO) => void;
}

const presenterCurationById = new Map(
    frontPresenterCuration.map((curation) => [curation.authorId, curation])
);

const presenterOrderById = new Map(
    [3, 16, 19, 7, 18, 66].map((authorId, index) => [authorId, index])
);

const getItemsPerView = (): number => {
    if (typeof window === "undefined") return 4;
    if (window.innerWidth < 760) return 1;
    if (window.innerWidth < 1008) return 2;
    return 4;
};

const presenterImage = (author: PublicAuthorDTO): string => {
    const avatar = author.Avatar?.trim();
    if (avatar) return avatar;

    const headerImage = author.HeaderImg?.trim();
    return headerImage && /^https?:\/\//i.test(headerImage) ? headerImage : "";
};

const presenterName = (author: PublicAuthorDTO): string =>
    author.Name?.trim() || `Presenter ${author.Id}`;

const presenterInitials = (name: string): string =>
    name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("");

const normalizeInstagramHandle = (value?: string | null): string => {
    const handle = value?.trim().replace(/^@/, "") ?? "";
    return /^[a-z0-9._]{1,30}$/i.test(handle) ? `@${handle}` : "";
};

const getInstagramProfile = (
    author: PublicAuthorDTO
): InstagramProfile | null => {
    const apiHandle = normalizeInstagramHandle(author.InstagramHandle);
    const apiProfileUrl = getSafeInstagramUrl(author.InstagramUrl);

    return apiHandle && apiProfileUrl
        ? { handle: apiHandle, profileUrl: apiProfileUrl }
        : null;
};

const PresenterCard: React.FC<
    Presenter & {
        index: number;
        presenterCount: number;
        onOpenReel?: (author: PublicAuthorDTO) => void;
    }
> = ({ author, curation, index, presenterCount, onOpenReel }) => {
    const name = presenterName(author);
    const image = presenterImage(author);
    const instagram = getInstagramProfile(author);
    const hasReelSource = Boolean(
        normalizeInstagramHandle(author.InstagramHandle) ||
            getInstagramReelEmbedUrl(author.InstagramLatestReelUrl)
    );
    const hasPlayableReel = Boolean(onOpenReel && hasReelSource);
    const headingId = `front-presenter-${author.Id}`;
    const role = curation?.role ?? "Ickonic author";

    const portrait = image ? (
        <img
            src={image}
            alt=""
            loading="lazy"
            onError={(event) => {
                event.currentTarget.hidden = true;
            }}
        />
    ) : null;

    return (
        <article
            className="front-presenters__card front-presenters__slide"
            role="group"
            aria-roledescription="slide"
            aria-label={`${index + 1} of ${presenterCount}: ${name}`}
        >
            <header className="front-presenters__profile">
                <div className="front-presenters__avatar" aria-hidden="true">
                    <span>{presenterInitials(name)}</span>
                    {portrait}
                </div>

                <div className="front-presenters__identity">
                    <p>{role}</p>
                    <h3 id={headingId}>{name}</h3>
                </div>
            </header>

            <div className="front-presenters__spotlight">
                <div className="front-presenters__portrait" aria-hidden="true">
                    <span>{presenterInitials(name)}</span>
                    {image ? (
                        <img
                            src={image}
                            alt=""
                            loading="lazy"
                            onError={(event) => {
                                event.currentTarget.hidden = true;
                            }}
                        />
                    ) : null}
                </div>
                <div className="front-presenters__shade" aria-hidden="true" />

                {instagram ? (
                    <a
                        className="front-presenters__instagram-link"
                        href={instagram.profileUrl}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={`View ${name} on Instagram (${instagram.handle})`}
                        title={instagram.handle}
                    >
                        <BsInstagram aria-hidden="true" focusable="false" />
                        <span>{instagram.handle}</span>
                    </a>
                ) : null}

                {hasPlayableReel ? (
                    <button
                        type="button"
                        className="front-presenters__reel-button"
                        onClick={() => onOpenReel?.(author)}
                        aria-label={`Watch ${name}'s latest Instagram reel`}
                    >
                        <BsPlayFill aria-hidden="true" focusable="false" />
                        <span>Watch latest reel</span>
                    </button>
                ) : null}
            </div>
        </article>
    );
};

const FrontPresentersSection: React.FC<FrontPresentersSectionProps> = ({
    onOpenReel,
}) => {
    const { authors, authorsLoading, authorsError } = useFetchPublicAuthors();
    const trackRef = useRef<HTMLDivElement>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [itemsPerView, setItemsPerView] = useState(getItemsPerView);

    const presenters: Presenter[] = authors
        .map((author, index) => ({
            author,
            curation: presenterCurationById.get(author.Id),
            order:
                presenterOrderById.get(author.Id) ??
                presenterOrderById.size + index,
        }))
        .sort((first, second) => first.order - second.order)
        .map(({ author, curation }) => ({ author, curation }));
    const maxIndex = Math.max(0, presenters.length - itemsPerView);

    useEffect(() => {
        const handleResize = () => setItemsPerView(getItemsPerView());
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        setCurrentIndex((index) => Math.min(index, maxIndex));
    }, [maxIndex]);

    const scrollToIndex = useCallback(
        (requestedIndex: number) => {
            const index = Math.max(0, Math.min(requestedIndex, maxIndex));
            const track = trackRef.current;
            const slide = track?.querySelectorAll<HTMLElement>(
                ".front-presenters__slide"
            )[index];

            setCurrentIndex(index);
            if (!track || !slide) return;

            const reducedMotion = window.matchMedia?.(
                "(prefers-reduced-motion: reduce)"
            ).matches;
            const scrollOptions: ScrollToOptions = {
                left: slide.offsetLeft,
                behavior: reducedMotion ? "auto" : "smooth",
            };

            if (typeof track.scrollTo === "function") {
                track.scrollTo(scrollOptions);
            } else {
                track.scrollLeft = scrollOptions.left ?? 0;
            }
        },
        [maxIndex]
    );

    const updateCurrentIndexFromScroll = () => {
        const track = trackRef.current;
        if (!track) return;

        const slides = Array.from(
            track.querySelectorAll<HTMLElement>(".front-presenters__slide")
        );
        if (!slides.length) return;

        const nearestIndex = slides.reduce((nearest, slide, index) => {
            const nearestDistance = Math.abs(
                slides[nearest].offsetLeft - track.scrollLeft
            );
            const distance = Math.abs(slide.offsetLeft - track.scrollLeft);
            return distance < nearestDistance ? index : nearest;
        }, 0);

        setCurrentIndex(Math.min(nearestIndex, maxIndex));
    };

    const handleCarouselKeyDown = (
        event: React.KeyboardEvent<HTMLDivElement>
    ) => {
        if (event.target !== event.currentTarget) return;

        let requestedIndex: number | null = null;

        if (event.key === "ArrowLeft" || event.key === "PageUp") {
            requestedIndex = currentIndex - 1;
        } else if (event.key === "ArrowRight" || event.key === "PageDown") {
            requestedIndex = currentIndex + 1;
        } else if (event.key === "Home") {
            requestedIndex = 0;
        } else if (event.key === "End") {
            requestedIndex = maxIndex;
        }

        if (requestedIndex === null) return;
        event.preventDefault();
        scrollToIndex(requestedIndex);
    };

    return (
        <section
            id="presenters"
            className="front-presenters"
            aria-labelledby="front-presenters-heading"
            aria-busy={authorsLoading}
            tabIndex={-1}
        >
            <div className="front-presenters__inner">
                <div className="front-presenters__intro">
                    <div>
                        <p className="front-presenters__kicker">
                            Presenters, in their own words
                        </p>
                        <h2 id="front-presenters-heading">
                            The people behind the questions.
                        </h2>
                    </div>
                    <p>
                        They do not all see the world the same way&mdash;and that
                        is the point. Meet filmmakers, researchers and presenters
                        bringing distinct experience and perspective to the
                        questions shaping our world. There is no party script and
                        no conclusion you are expected to inherit.
                    </p>
                </div>

                {authorsLoading ? (
                    <>
                        <p className="front-presenters__status" role="status">
                            {"Loading presenters\u2026"}
                        </p>
                        <div
                            className="front-presenters__carousel-track front-presenters__carousel-track--loading"
                            aria-hidden="true"
                        >
                            {[0, 1, 2, 3].map((index) => (
                                <div
                                    className="front-presenters__card front-presenters__card--loading"
                                    key={index}
                                />
                            ))}
                        </div>
                    </>
                ) : authorsError || presenters.length === 0 ? (
                    <p className="front-presenters__empty" role="status">
                        {authorsError || "Presenter profiles are being prepared."}
                    </p>
                ) : (
                    <div className="front-presenters__carousel">
                        <p
                            id="front-presenters-instructions"
                            className="front-presenters__status"
                        >
                            Use the previous and next buttons, arrow keys, or
                            swipe to explore the presenters.
                        </p>

                        <div className="front-presenters__carousel-toolbar">
                            {presenters.length > itemsPerView ? (
                                <ButtonsPrevNext
                                    onPrev={() =>
                                        scrollToIndex(currentIndex - 1)
                                    }
                                    onNext={() =>
                                        scrollToIndex(currentIndex + 1)
                                    }
                                    currentIndex={currentIndex}
                                    maxIndex={maxIndex}
                                    parentClass="front-presenters__controls"
                                />
                            ) : null}
                        </div>

                        <div
                            ref={trackRef}
                            className="front-presenters__carousel-track"
                            role="group"
                            aria-roledescription="carousel"
                            aria-label="Presenter carousel"
                            aria-describedby="front-presenters-instructions"
                            tabIndex={0}
                            onKeyDown={handleCarouselKeyDown}
                            onScroll={updateCurrentIndexFromScroll}
                        >
                            {presenters.map((presenter, index) => (
                                <PresenterCard
                                    key={presenter.author.Id}
                                    {...presenter}
                                    index={index}
                                    presenterCount={presenters.length}
                                    onOpenReel={onOpenReel}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

export default FrontPresentersSection;
